<script setup lang="ts">
import { useEventListener, useThrottleFn } from '@vueuse/core'

const rootMenuOpen = ref(false)
const searchPanelOpen = ref(false)
const uiStore = useUIStore()
let lastScrollTop = 0

const { storesUrl, getLocalePrefix } = useSiteUrl()

const locale = useI18n().locale.value

const { data } = await useAsyncData('siteHeader', handler, {
  deep: false,
  transform: transformer,
})

async function handler() {
  const { getEntries } = useContentful()
  const entries = await getEntries({
    content_type: 'siteHeader',
    'fields.locale': localeConfig[locale].code,
    include: 8,
  })
  return entries?.items
}

function sectionPanelsTransformer(panels: any) {
  type PanelType = 'utilityPanel' | 'richTextPanel' | 'sectionPanel'

  return panels?.map((panel: any) => {
    const id = panel?.sys?.id
    const type: PanelType = panel?.sys?.contentType?.sys?.id
    const actions = panel?.fields?.actions
    const title = panel?.fields?.title
    const content = panel?.fields?.content
    const supportingData = panel?.fields?.supportingData?.home
    const personalizationTags = panel?.fields?.personalizationTags

    // Handle nested sectionPanel (3rd layer)
    if (type === 'sectionPanel') {
      const nestedPanels = sectionPanelsTransformer(panel?.fields?.panels)
      const nestedPanelLink = panel?.fields?.references?.[0]?.fields

      return {
        id,
        type,
        title: panel?.fields?.title,
        panels: nestedPanels,
        panelLink: nestedPanelLink,
        personalizationTags,
      }
    }

    return {
      id,
      type,
      actions,
      title,
      content,
      supportingData,
      personalizationTags,
    }
  })
}

function headerLinksTransformer(headerLinks: any) {
  return headerLinks?.map((item: any) => {
    const id = item?.sys?.id
    const link = item?.fields?.link?.fields
    const support = item?.fields?.supportingData?.home
    const sectionPanel = item?.fields?.sectionPanel?.fields

    return {
      id,
      link: {
        text: link?.text,
        url: link?.url,
      },
      menuStyle: support?.linkStyle?.value,
      sectionPanel: sectionPanelsTransformer(sectionPanel?.panels),
      sectionPanelLink: sectionPanel?.references?.[0]?.fields,
    }
  })
}

function transformer(data: any) {
  const localeData = data.find((item: any) => item?.fields?.locale === localeConfig[locale].code)
  const {
    eyebrow,
    headerLinks,
    tacticalBannerEngagementWedding,
    tacticalBannerGeneralContent,
    tacticalBannerHomePage,
    tacticalBannerJewelleryAndOtherPLPS,
    tacticalBannerPDPS,
    tacticalBannerSearchResults,
    popularCategories,
    searchProductRecommender,
  } = localeData?.fields ?? {}

  return {
    popularCategories: popularCategories?.fields?.actions || [],
    searchProductRecommender: searchProductRecommender?.fields || {},
    navigationItems: headerLinksTransformer(headerLinks),
    eyebrow,
    tacticalBannerEngagementWedding: filterPublished(tacticalBannerEngagementWedding),
    tacticalBannerJewelleryAndOtherPLPS: filterPublished(tacticalBannerJewelleryAndOtherPLPS),
    tacticalBannerHomePage: filterPublished(tacticalBannerHomePage),
    tacticalBannerGeneralContent: filterPublished(tacticalBannerGeneralContent),
    tacticalBannerPDPS: filterPublished(tacticalBannerPDPS),
    tacticalBannerSearchResults: filterPublished(tacticalBannerSearchResults),
  }
}

function getMoustacheData(data: any) {
  const { path: routePath } = useRoute()
  // Strip the locale prefix (eg. /fr) so the path rules below match the same pages across locales
  const localePrefix = getLocalePrefix()
  const path =
    localePrefix && (routePath === localePrefix || routePath.startsWith(`${localePrefix}/`))
      ? routePath.slice(localePrefix.length) || '/'
      : routePath

  if (path.match(/^\/+$/)) {
    return data?.tacticalBannerHomePage
  }
  if (path.match(/\/engagement*|\/wedding*|\/collections\/bridal/gi) && !path.match(/\/p\//gi)) {
    return data?.tacticalBannerEngagementWedding
  }
  if (path.match(/\/jewellery*|\/gifts*|\/sale*|\/collections\/fashion/gi) && !path.match(/\/jewellery-builder/gi)) {
    return data?.tacticalBannerJewelleryAndOtherPLPS
  }
  if (path.match(/\/p\//gi)) {
    return data?.tacticalBannerPDPS
  }
  if (path.match(/\/search/gi)) {
    return data?.tacticalBannerSearchResults
  }
  if (!path.match(/\/jewellery-builder|\/checkout\//gi)) {
    return data?.tacticalBannerGeneralContent
  }
  return null
}

function trackMenuOpenEvent(e: Event) {
  pushEventToDataLayer('custom.menu.open', {
    group: 'mobile menu',
    label: 'symbol-menu',
    info: {
      location: 'sidebar',
      element: e?.target instanceof Element ? e.target.tagName : undefined,
    },
  })
}

function trackMenuCloseEvent(e: Event) {
  pushEventToDataLayer('custom.menu.close', {
    group: 'mobile menu',
    label: 'close button',
    info: {
      location: 'sidebar',
      element: e?.target instanceof Element ? e.target.tagName : undefined,
    },
  })
}

function openRootMenu(e: Event) {
  rootMenuOpen.value = true
  trackMenuOpenEvent(e)
  document?.documentElement?.classList?.add('scroll-lock')
}

function closeRootMenu(e: Event) {
  rootMenuOpen.value = false
  trackMenuCloseEvent(e)
  document?.documentElement?.classList?.remove('scroll-lock')
}

onMounted(() => {
  useEventListener(document, 'keyup', (e) => {
    /* Page level event listener, Component level listener doesn't work in this use case  */
    if (e.key === 'Escape') {
      searchPanelOpen.value = false
    }
  })

  const SCROLL_OFFSET = 120 /* Enough scroll to cover the whitespace left by Header + Moustache  */
  const throttledScrollHandler = useThrottleFn(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    if (scrollTop > lastScrollTop && scrollTop > SCROLL_OFFSET) {
      uiStore.isHeaderHidden = true
    } else {
      uiStore.isHeaderHidden = false
    }
    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop
  }, 100)

  useEventListener(window, 'scroll', throttledScrollHandler, { passive: true })
})

function openSearchPanel() {
  searchPanelOpen.value = true
  document?.documentElement?.classList?.add('scroll-lock')
}

function closeSearchPanel() {
  searchPanelOpen.value = false
  document?.documentElement?.classList?.remove('scroll-lock')
}
</script>

<template>
  <header class="header" :class="{ 'header--hidden': uiStore.isHeaderHidden }" @keyup.escape="rootMenuOpen = false">
    <a href="#main" class="header-skip__link sr-only">{{ $t('screen_reader.buttons.skip_to_content') }}</a>
    <Eyebrow :data="data?.eyebrow" />

    <nav>
      <div class="container">
        <div class="header__left">
          <button aria-label="Menu" class="header__hamburger" data-testid="header-menu-toggle" @click="openRootMenu">
            <svg-icon name="mhj:hamburger" />
          </button>
          <Anchor :title="$t('home_text')" :url="getLocalePrefix() + '/'" class="header__logo">
            <svg-icon name="mhj:logo" />
          </Anchor>
        </div>

        <Menu
          :rootMenu="data?.navigationItems"
          :rootMenuOpen="rootMenuOpen"
          @root-menu-close="closeRootMenu"
          @root-menu-hover="closeSearchPanel"
        />
        <div class="header__menu-overlay" v-if="rootMenuOpen" @click="closeRootMenu" />

        <ul class="header__nav-icons">
          <li>
            <Search
              iconClass="header__nav-icon-search"
              :popularCategories="data?.popularCategories"
              :searchProductRecommender="data?.searchProductRecommender"
              :searchPanelOpen="searchPanelOpen"
              @open-search-panel="openSearchPanel"
              @close-search-panel="closeSearchPanel"
            />
          </li>
          <li>
            <Anchor class="header__nav-icon-stores" aria-label="Location" :url="storesUrl">
              <svg-icon name="mhj:location-on" />
            </Anchor>
          </li>
          <li>
            <UserAccount :hideLabel="true" class="header__nav-icon-user" />
          </li>
          <li>
            <Wishlist
              :ga-options="{ eventName: 'custom.menu.link' }"
              class="header__nav-icon-wishlist"
              test-id="header-wishlist"
            />
          </li>
          <li>
            <Bag buttonClass="header__nav-icon-bag" />
          </li>
        </ul>
      </div>
    </nav>
  </header>

  <div class="header-border" :class="{ 'header--hidden': uiStore.isHeaderHidden }"></div>

  <Moustache :class="{ 'header--hidden': uiStore.isHeaderHidden }" :data="getMoustacheData(data)" />
</template>

<style lang="postcss">
.header {
  --header-menu-height: 3.4rem;
  /* Menu Height */
  height: var(--header-height);
  position: fixed;
  width: 100vw;
  /* avoid page shift on scroll-lock */
  top: 0;
  z-index: var(--z-header);
  background: var(--surface-light-00);
  transition: transform 0.3s ease-in-out;

  .header-skip__link {
    position: absolute;
    top: 0;
    left: 50%;
    translate: -50% 5%;
    background: var(--surface-dark-10);
    color: var(--text-reverse);
    padding: var(--space-20) var(--space-50);
    z-index: 1;
  }

  .header__menu-overlay {
    position: fixed;
    inset: 0 0 0 0;
    background: var(--mask-bg-dark);
    z-index: var(--z-header-menu-overlay);
  }

  nav {
    > .container {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .header__nav-icons,
  .header__left {
    display: flex;
  }

  .header__nav-icons {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-right: var(--space-30);
    /* avoid clipping of Cart Count on small desktop/tab  */
  }

  .header__nav-icon-search,
  .header__nav-icon-stores,
  .header__nav-icon-user,
  .header__nav-icon-bag {
    height: var(--header-menu-height);
    display: flex;
    align-items: center;
    padding: 0.5rem 0;
    cursor: pointer;
    transition: var(--transition-0);
    outline-offset: -0.1rem;
    min-width: 1.5rem;

    /* &:hover {
        color: var(--text-link-hover);
        transform: scale(1.1);
      }

      &:active {
        transition: none;
        transform: scale(1);
      } */
  }

  .header__hamburger {
    margin-right: 1rem;
    height: var(--header-menu-height);
  }

  .header__logo {
    display: flex;
    align-items: center;

    > svg {
      width: 8rem;
      transform: none !important;
    }
  }

  @media (--mobile-only) {
    nav {
      .header__nav-icon-stores,
      .header__nav-icon-wishlist {
        display: none;
      }
    }
  }

  @media (--tablet) {
    .header__nav-icons {
      gap: var(--space-50);
    }
  }

  @media (--desktop) {
    .header__hamburger {
      display: none;
    }

    .header__logo > svg {
      width: 9.3125rem;
      /* 149px */
    }
  }
}

.header-border {
  position: fixed;
  width: 100%;
  height: var(--header-border-height);
  background: var(--surface-accent);
  top: 5.3rem;
  z-index: var(--z-header-border);
  transition: top 0.3s ease-in-out;

  & + .moustache {
    position: fixed;
    width: 100vw;
    /* avoid page shift on scroll-lock */
    top: 5.6rem;
    transition: top 0.3s ease-in-out;
  }
}

.header.header--hidden {
  @media (--mobile-only) {
    /* Hack to get around very strange bug where screen goes black if this transform happens when the search bar or menu is open so we just do not allow this
    In most cases we use overscroll-behavior: contain; on the panels to prevent this but on wide mobile devices where the mask is visible its possible to
    attempt to scroll on the fake mask so no way to block the scroll in the mask which then scrolls the body without a full rewrite to use a proper dialog */
    &:not(:has(.search--open), :has(.menu--open)) {
      transform: translateY(-100%);
    }
  }
}

.header--hidden.header-border {
  top: -0.5rem;
}

.header--hidden.moustache {
  top: -100%;
}

@media (--tablet) {
  .header.header--hidden {
    transform: none;
  }

  .header--hidden.header-border {
    top: 5.3rem;
  }
}
</style>
